import { Koi } from '@ksyos/koi';
import { ComorbiditiesChoice, Retrognathism, TongueSize, TonsilSize, YesOrNoOrOther } from '@common/enums';
import { addressValidationFinalSchema } from '@common/validation/address';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { emailSchema, gewichtInKgSchema, lengteInCmSchema } from '@common/validation/schemas';
import { identifierSocialSecuritySchemaFinal } from '@common/validation/schemas/identifierSocialSecuritySchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { IOsasReferralData } from '../osasReferralSchemas/osasReferralSchema';
import { processActivityMedicationDataSchema } from '../../processActivityMedicationData';
import { processActivityProfessionalSummaryDataSchema } from '../../processActivityProfessionalSummaryData';
import { intoxicationSchema } from '../intoxicationSchemas';
import { comorbiditiesSchema, osasReferralDraftSchema } from '../osasReferralSchemas/osasReferralSchema';
import { SleepRecorderType } from '../sleepRecorderType';

export type OsasZorgDomeinReferralCustomControlData = IOsasReferralData & {
    isPatientInformed: boolean;
    referrerOrganizationId: string | null;
    referrerPractitionerId: string | null;
};
export const osasZorgDomeinReferralSchemaDraft = osasReferralDraftSchema.concat(
    Koi.object({
        isPatientInformed: Koi.boolean().optional(),
        referrerOrganizationId: Koi.string().uuid().allow(null),
        referrerPractitionerId: Koi.string().uuid().allow(null),
    })
);
export const osasZorgDomeinReferralSchemaFinal = Koi.object({
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    comorbidities: comorbiditiesSchema,
    comorbidityChoice: Koi.koi().enum(ComorbiditiesChoice).allow(null),
    draftCareProviderOrganizationId: Koi.string().uuid(),
    draftCareProviderPractitionerId: Koi.string().uuid(),
    hasDentures: Koi.koi().enum(YesOrNoOrOther).allow(null),
    importantComplaints: Koi.object({
        breathStops: Koi.boolean(),
        other: getTextAreaStringSchema(true, 255),
        sleepiness: Koi.boolean(),
        snoring: Koi.boolean(),
        tiredness: Koi.boolean(),
    }),
    isDentitionWellSanitized: Koi.koi().enum(YesOrNoOrOther).allow(null),
    isDentitionWellSanitizedRemark: getTextAreaStringSchema(true),
    isPatientInformed: Koi.boolean(),
    lengthInCm: lengteInCmSchema.allow(null),
    patient: Koi.object({
        emailAddress: emailSchema,
        homePhoneNumber: getTextAreaStringSchema(true, 255),
        identifierSocialSecurity: identifierSocialSecuritySchemaFinal,
        mobileNumber: getTextAreaStringSchema(true, 255),
    }).concat(addressValidationFinalSchema),
    referralReason: getTextAreaStringSchema(true, 255),
    referrerOrganizationId: Koi.string().uuid(),
    referrerPractitionerId: Koi.string().uuid(),
    retrognathism: Koi.koi().enum(Retrognathism).allow(null),
    sleepRecorderType: Koi.koi().enum(SleepRecorderType),
    tongueSize: Koi.koi().enum(TongueSize).allow(null),
    tonsilSize: Koi.koi().enum(TonsilSize).allow(null),
    weightInKg: gewichtInKgSchema.allow(null),
})
    .concat(processActivityProfessionalSummaryDataSchema)
    .concat(processActivityMedicationDataSchema)
    .concat(intoxicationSchema);
